import RosterRequestsPage from './pages/RosterRequestsPage';
// ============================================================
// ADMIN APP — Router + Login Page
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useAdminStore from './hooks/useAdmin';
import AdminLayout from './components/layout/Layout';
import DashboardPage from './pages/Dashboard';
import SetupPage from './pages/Setup';
import AdminsPage from './pages/Admins';
import { AllDoctorsPage, PendingDoctorsPage } from './pages/Doctors';
import { PatientsPage,
         BillingPage, PayoutsPage, ServicesPage } from './pages/AllPages';
import RotaPage from './pages/RotaPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AppointmentsCalendarPage from './pages/AppointmentsCalendarPage'; /* APPT_CALENDAR_IMPORT_2026-08-05 */
import DoctorProfilePage from './pages/DoctorProfilePage';
import PatientProfilePage from './pages/PatientProfilePage';
import ConsultationDetailPage from './pages/ConsultationDetailPage';
import MonthlyClearingsPage from './pages/MonthlyClearings'; /* @d4-clearings-import */
import MonthlyClearingsRunPage from './pages/MonthlyClearingsRun'; /* @d4-preview-wiring */
import DoctorInvoicesPage from './pages/DoctorInvoices';
import PlatformConfigPage from './pages/PlatformConfig';
import DiscountCodesPage from './pages/DiscountCodesPage'; // @discount-codes-portal-v1
import DoctorAttendancePage from './pages/DoctorAttendancePage'; // @attendance-portal-v1
import RenewalsReportPage from './pages/RenewalsReportPage'; // @chronic-renewal-v1
import SickLeaveReportPage from './pages/SickLeaveReportPage'; // @sick-leave-v1
import BloodTestsReportPage from './pages/BloodTestsReportPage'; // @blood-tests-v1
import PrepReportPage from './pages/PrepReportPage'; // @prep-v1
import ClinicInterestPage from './pages/ClinicInterestPage'; // @clinic-interest-report-v1
import { dashboardAPI } from './services/api';
import './styles/globals.css';
import styles from './App.module.css';
import InactivityWarning from './components/InactivityWarning';
import ForgotPasswordModal from './components/ForgotPasswordModal'; // @forgot-password-import
import { startInactivityTracking, extendSession, clearAuth, isIdleExpired, isTokenExpired, isTokenExpiringSoon, getRefreshToken, storeTokens } from './services/authManager';
import axios from 'axios';

// ── PROTECTED ROUTE ──
// Verifies token freshness on mount. If token is expired/expiring,
// refreshes it BEFORE rendering protected content. Prevents 401-storm
// rate limiting after long idle periods or post-deploy reloads.
function Protected({ children }) {
  const { isAuthenticated, logout } = useAdminStore();
  const [bootChecked, setBootChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const checkAndRefresh = async () => {
      if (!isAuthenticated) { setBootChecked(true); return; }
      // @s11-idle-enforced-at-boot (9 Oct 2026)
      // Enforce the 30-minute idle rule on app start, BEFORE the token checks
      // below. Without this, a browser closed for days reopened to a stale
      // access token, which the refresh branch below silently renewed with the
      // 30-day refresh token - so the admin was signed back in and the idle
      // rule only ever applied to a tab left open.
      //
      // This must stay ahead of the authenticated layout that calls
      // startInactivityTracking(), because that function's first act is to
      // overwrite lastActivity with now.
      if (isIdleExpired()) {
        clearAuth();
        if (!cancelled) await logout();
        if (!cancelled) setBootChecked(true);
        return;
      }
      // Token still fresh — no need to refresh
      if (!isTokenExpired() && !isTokenExpiringSoon()) {
        setBootChecked(true);
        return;
      }
      // Token expired or about to — try refresh once
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearAuth();
        if (!cancelled) await logout();
        if (!cancelled) setBootChecked(true);
        return;
      }
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
        const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
        const { accessToken, refreshToken: newRefreshToken } = data.data || data;
        storeTokens(accessToken, newRefreshToken);
        if (!cancelled) setBootChecked(true);
      } catch (err) {
        clearAuth();
        if (!cancelled) await logout();
        if (!cancelled) setBootChecked(true);
      }
    };
    checkAndRefresh();
    return () => { cancelled = true; };
  }, [isAuthenticated, logout]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!bootChecked) return <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Φόρτωση...</div>;
  return children;
}

// ── WITH LAYOUT ──
function WithLayout({ children }) {
  const { isAuthenticated } = useAdminStore();
  const [alerts, setAlerts] = useState({ '/doctors/pending': 0, '/slots/pending': 0 });

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchAlerts = () => {
      dashboardAPI.getStats()
        .then(res => {
          const s = res.data.data || {};
          setAlerts({
            '/doctors/pending': s.pendingDoctors || 0,
            '/slots/pending': s.pendingSlots || 0,
          });
        })
        .catch(() => { /* @m24-silence: pre-existing swallow, kept as-is when linting was switched on 25 Sep 2026; why it is safe has not been established */ });
    };
    fetchAlerts();
    // refresh every 60 seconds
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  return (
    <Protected>
      <AdminLayout alerts={alerts}>
        {children}
      </AdminLayout>
    </Protected>
  );
}

// ── LOGIN PAGE ──
function LoginPage() {
  const { login, verify2FA, isLoading, error, clearError, isAuthenticated } = useAdminStore();
  const [step,     setStep]     = useState(1);
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [userId,   setUserId]   = useState(null);
  const [code,     setCode]     = useState(['','','','','','']);
  const [showPass, setShowPass] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [attemptError, setAttemptError] = useState('');
  const [showForgot, setShowForgot] = useState(false); // @forgot-password-state
  const codeRefs = useRef([]);
  const maxAttempts = 3;

  // @m24-deps: dependency list left exactly as it was when linting was switched on 25 Sep 2026.
  // Adding the missing dependency can change how often this effect runs, so it is a behaviour
  // change to make deliberately and test, not a lint tidy-up. Recorded, not silenced by turning
  // the rule off - it still guards every effect written from here on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { clearError(); setAttempts(0); setAttemptError(''); }, [step]);

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const handleEmail = (e) => { e.preventDefault(); if (email) setStep(2); };

  const handlePassword = async (e) => {
    e.preventDefault();
    try { const r = await login(email, password); setUserId(r.userId); setStep(3); setTimeout(()=>codeRefs.current[0]?.focus(),100); }
    catch { /* @m24-silence: pre-existing swallow, kept as-is when linting was switched on 25 Sep 2026; why it is safe has not been established */ }
  };

  const handleCodeInput = (i, v) => {
    if (!/^\d*$/.test(v)) return;
    const nc = [...code]; nc[i] = v.slice(-1); setCode(nc);
    setAttemptError('');
    if (v && i < 5) codeRefs.current[i+1]?.focus();
  };

  const handleVerify = async () => {
    const full = code.join('');
    if (full.length !== 6) return;
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    try {
      await verify2FA(userId, full);
    } catch {
      if (newAttempts >= maxAttempts) {
        setAttemptError('3/3 — Αποτυχία σύνδεσης. Παρακαλώ ξεκινήστε από την αρχή.');
        setTimeout(() => {
          setStep(1);
          setCode(['','','','','','']);
          setPassword('');
          setAttempts(0);
          setAttemptError('');
        }, 2000);
      } else {
        setAttemptError(`${newAttempts}/${maxAttempts} — Λάθος κωδικός. Δοκιμάστε ξανά.`);
        setCode(['','','','','','']);
        codeRefs.current[0]?.focus();
      }
    }
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginLeft}>
        <div className={styles.loginBrand}>
          <div className={styles.logoCross} />
          <span className={styles.logoName}>Doc<span>Now</span></span>
        </div>
        <div className={styles.loginTagline}>
          Admin<br /><em>Portal</em>
        </div>
        <p className={styles.loginSub}>Πύλη διαχείρισης της πλατφόρμας DocNow. Πρόσβαση μόνο για εξουσιοδοτημένο προσωπικό.</p>
        <div className={styles.loginStats}>
          <div><div className={styles.sNum}>ADMIN</div><div className={styles.sLbl}>Access Level</div></div>
          <div><div className={styles.sNum}>2FA</div><div className={styles.sLbl}>Protected</div></div>
          <div><div className={styles.sNum}>GDPR</div><div className={styles.sLbl}>Compliant</div></div>
        </div>
      </div>

      <div className={styles.loginRight}>
        <div className={styles.loginForm}>
          <h2 className={styles.loginTitle}>
            {step===1?'Admin Portal':step===2?'Κωδικός Πρόσβασης':'Επαλήθευση 2FA'}
          </h2>
          <p className={styles.loginSub2}>{step===1?'Συνδεθείτε στην πύλη διαχείρισης':step===2?email:'Κωδικός επαλήθευσης'}</p>

          {(error || attemptError) && <div className={styles.errBox}>⚠️ {attemptError || error}</div>}

          {step===3 && attempts > 0 && !attemptError && (
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
              {[1,2,3].map(i=>(
                <div key={i} style={{width:10,height:10,borderRadius:5,backgroundColor:i<=attempts?'#C0185A':'#E8ECF2'}} />
              ))}
              <span style={{fontSize:13,color:'var(--muted)'}}>{attempts}/{maxAttempts} απόπειρες</span>
            </div>
          )}

          {step===1 && (
            <form onSubmit={handleEmail}>
              <div className={styles.fGroup}><label className={styles.fLabel}>Email</label><input className={styles.fInput} type="email" placeholder="admin@docnow.gr" value={email} onChange={e=>setEmail(e.target.value)} autoFocus required /></div>
              <button className={styles.fBtn} type="submit" disabled={!email}>Συνέχεια →</button>
            </form>
          )}

          {step===2 && (
            <form onSubmit={handlePassword}>
              <div className={styles.fGroup}>
                <label className={styles.fLabel}>Κωδικός</label>
                <div style={{position:'relative'}}>
                  <input className={styles.fInput} type={showPass?'text':'password'} style={{paddingRight:44}} placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} autoFocus required />
                  <button type="button" style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',fontSize:18,cursor:'pointer',color:'var(--muted)'}} onClick={()=>setShowPass(!showPass)}>{showPass?'🙈':'👁'}</button>
                </div>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:14}}><button type="button" className={styles.fLink} onClick={()=>setStep(1)}>← Πίσω</button><button type="button" className={styles.fLink} onClick={()=>setShowForgot(true)} data-sentinel="@forgot-password-link">Ξέχασα τον κωδικό</button></div>
              <button className={styles.fBtn} type="submit" disabled={!password||isLoading}>{isLoading?'Σύνδεση...':'Σύνδεση →'}</button>
            </form>
          )}

          {step===3 && (
            <div>
              <div className={styles.codeInputs}>
                {code.map((d,i)=>(
                  <input key={i} ref={el=>codeRefs.current[i]=el} className={styles.codeInput} type="text" inputMode="numeric" maxLength={1} value={d}
                    onChange={e=>handleCodeInput(i,e.target.value)}
                    onKeyDown={e=>e.key==='Backspace'&&!d&&i>0&&codeRefs.current[i-1]?.focus()}
                    disabled={attempts>=maxAttempts}
                  />
                ))}
              </div>
              <button className={styles.fBtn} onClick={handleVerify} disabled={code.some(c=>!c)||isLoading||attempts>=maxAttempts}>
                {isLoading?'Επαλήθευση...':'Επαλήθευση & Σύνδεση →'}
              </button>
            </div>
          )}

          <ForgotPasswordModal
            open={showForgot}
            onClose={() => setShowForgot(false)}
            initialEmail={email}
            data-sentinel="@forgot-password-mount"
          />
          <p className={styles.loginNote}>🔒 Ασφαλής πρόσβαση · Κρυπτογράφηση 256-bit</p>
        </div>
      </div>
    </div>
  );
}
// ── MAIN APP ──
export default function App() {
  const [showWarning, setShowWarning] = React.useState(false);
  const { isAuthenticated, logout } = useAdminStore();

  React.useEffect(() => {
    if (!isAuthenticated) return;

    const cleanup = startInactivityTracking(
      () => setShowWarning(true),
      () => {
        clearAuth();
        logout();
        window.location.href = '/login';
      }
    );

    return cleanup;
  }, [isAuthenticated, logout]);

  const handleStayLoggedIn = () => {
    extendSession();
    setShowWarning(false);
  };

  const handleLogout = () => {
    clearAuth();
    logout();
    window.location.href = '/login';
  };

  return (
    <BrowserRouter>
      <Toaster position="bottom-right" toastOptions={{ style:{ background:'#0F1F3D',color:'white',fontFamily:'DM Sans, sans-serif',fontSize:'14px',borderRadius:'10px',padding:'12px 18px' } }} />
      <InactivityWarning 
        show={showWarning} 
        onStayLoggedIn={handleStayLoggedIn} 
        onLogout={handleLogout}
        remainingSeconds={300}
      />
      <Routes>
        <Route path="/login"           element={<LoginPage />} />
        <Route path="/setup"           element={<SetupPage />} />
        <Route path="/platform-config" element={<WithLayout><PlatformConfigPage /></WithLayout>} /> {/* PLATFORM_CONFIG_ROUTE_2026-07-06 */}
        <Route path="/dashboard"       element={<WithLayout><DashboardPage /></WithLayout>} />
        <Route path="/doctors"         element={<WithLayout><AllDoctorsPage /></WithLayout>} />
        <Route path="/doctors/pending" element={<WithLayout><PendingDoctorsPage /></WithLayout>} />
        <Route path="/doctors/:doctorId" element={<WithLayout><DoctorProfilePage /></WithLayout>} />
        <Route path="/patients"        element={<WithLayout><PatientsPage /></WithLayout>} />
        <Route path="/patients/:patientId" element={<WithLayout><PatientProfilePage /></WithLayout>} />
        <Route path="/consultations/:appointmentId" element={<WithLayout><ConsultationDetailPage /></WithLayout>} />
        <Route path="/rota"            element={<WithLayout><RotaPage /></WithLayout>} />
        <Route path="/slots/pending"   element={<WithLayout><RosterRequestsPage /></WithLayout>} />
        <Route path="/billing"         element={<WithLayout><BillingPage /></WithLayout>} />
        <Route path="/payouts"         element={<WithLayout><PayoutsPage /></WithLayout>} />
        <Route path="/clearings"       element={<WithLayout><MonthlyClearingsPage /></WithLayout>} /> {/* @d4-clearings-route */}
        <Route path="/clearings-run"   element={<WithLayout><MonthlyClearingsRunPage /></WithLayout>} /> {/* @d4-preview-wiring */}
        <Route path="/doctor-invoices" element={<WithLayout><DoctorInvoicesPage /></WithLayout>} /> {/* DOCTOR_INVOICES_ROUTE_2026-07-06 */}
        <Route path="/discount-codes"  element={<WithLayout><DiscountCodesPage /></WithLayout>} /> {/* @discount-codes-portal-v1 */}
        <Route path="/attendance"      element={<WithLayout><DoctorAttendancePage /></WithLayout>} /> {/* @attendance-portal-v1 */}
        <Route path="/renewals"        element={<WithLayout><RenewalsReportPage /></WithLayout>} /> {/* @chronic-renewal-v1 */}
        <Route path="/sick-leave"        element={<WithLayout><SickLeaveReportPage /></WithLayout>} /> {/* @sick-leave-v1 */}
        <Route path="/blood-tests"       element={<WithLayout><BloodTestsReportPage /></WithLayout>} /> {/* @blood-tests-v1 */}
        <Route path="/prep"              element={<WithLayout><PrepReportPage /></WithLayout>} /> {/* @prep-v1 */}
        <Route path="/clinic-interest" element={<WithLayout><ClinicInterestPage /></WithLayout>} /> {/* @clinic-interest-report-v1 */}
        <Route path="/analytics"       element={<WithLayout><AnalyticsPage /></WithLayout>} />
        <Route path="/appointments"    element={<WithLayout><AppointmentsCalendarPage /></WithLayout>} /> {/* APPT_CALENDAR_ROUTE_2026-08-05 */}
        <Route path="/services"        element={<WithLayout><ServicesPage /></WithLayout>} />
        <Route path="/admins"          element={<WithLayout><AdminsPage /></WithLayout>} />
        <Route path="*"                element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
